import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useUserContext } from "../../contexts";
import { useLocation, Link } from "react-router-dom";
import Navbar from "../Nav";
import PageInfo from "../PageInfo/PageInfo";
import styles from "./stats.module.scss";

const UserStatsPage = () => {
  const { userId } = useUserContext();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState('overall');
  const location = useLocation();

  // All available sports
  const allSports = [
    { id: 'overall', label: 'Overall', key: null },
    { id: '1', label: 'NBA', key: 'nba' },
    { id: '7', label: 'NFL', key: 'nfl' },
    { id: '3', label: 'NHL', key: 'nhl' },
    { id: '5', label: 'MLB', key: 'mlb' },
    { id: '12', label: 'Movies', key: 'movies' },
    { id: '14', label: 'Wrestling', key: 'wrestling' },
  ];

  // Filter sports to only show those the user has played (or Overall)
  const sports = stats ? allSports.filter(sport =>
    sport.id === 'overall' || (stats.sport_elos && stats.sport_elos[sport.key])
  ) : allSports;

  useEffect(() => {
    const fetchData = async () => {
      const API_URL = process.env.REACT_APP_API_URL || 'https://crossovergames-8b4eb8e6663f.herokuapp.com';
      const token = localStorage.getItem('AUTH_TOKEN') || localStorage.getItem('authToken');

      console.log('🔍 UserStats - Fetching data...');
      console.log('API_URL:', API_URL);
      console.log('userId:', userId);
      console.log('token exists:', !!token);

      if (!token || !userId) {
        console.error('❌ Missing token or userId');
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      try {
        const sportParam = selectedSport === 'overall' ? '' : `?sport_id=${selectedSport}`;
        const historySportParam = selectedSport === 'overall' ? '' : `&sport=${selectedSport}`;
        console.log('📡 Fetching stats from:', `${API_URL}/api/multiplayer/stats${sportParam}`);
        console.log('📡 Fetching history from:', `${API_URL}/api/multiplayer/history?limit=5${historySportParam}`);

        // Fetch stats and history in parallel
        const [statsRes, historyRes] = await Promise.all([
          fetch(`${API_URL}/api/multiplayer/stats${sportParam}`, { headers, credentials: 'include' }),
          fetch(`${API_URL}/api/multiplayer/history?limit=5${historySportParam}`, { headers, credentials: 'include' })
        ]);

        console.log('📊 Stats response status:', statsRes.status);
        console.log('📜 History response status:', historyRes.status);

        if (statsRes.ok) {
          const data = await statsRes.json();
          console.log('✅ Stats data:', data);
          setStats(data.stats);
        } else {
          console.error('❌ Stats fetch failed:', statsRes.status, await statsRes.text());
        }

        if (historyRes.ok) {
          const data = await historyRes.json();
          console.log('✅ History data:', data);
          setHistory(data.games || []);
        } else {
          console.error('❌ History fetch failed:', historyRes.status, await historyRes.text());
        }
      } catch (error) {
        console.error('💥 Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, selectedSport]);

  if (loading) {
    return (
      <div className={styles.page}>
        <Navbar />
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={styles.page}>
        <Navbar />
        <div className={styles.container}>
          <h1>Multiplayer Stats</h1>
          <p>Please log in to view your stats.</p>
        </div>
      </div>
    );
  }

  const sportMap = {
    '1': 'nba',
    '7': 'nfl',
    '3': 'nhl',
    '5': 'mlb',
  };

  // Get sport-specific data based on selected sport
  const getSportData = () => {
    if (selectedSport === 'overall') {
      return {
        wins: stats.wins,
        losses: stats.losses,
        draws: stats.draws,
        totalGames: stats.total_games,
        winRate: stats.win_rate || (stats.total_games > 0 ? ((stats.wins / stats.total_games) * 100).toFixed(1) : 0),
        elo: stats.overall_elo,
      };
    }

    const sportKey = sportMap[selectedSport];
    const sportRecord = stats.record_by_sport?.[sportKey];
    const sportGames = stats.games_by_sport?.[sportKey] || 0;

    // Calculate sport-specific win rate
    const sportWinRate = sportRecord && sportGames > 0
      ? ((sportRecord.wins / sportGames) * 100).toFixed(1)
      : 0;

    return {
      wins: sportRecord?.wins || 0,
      losses: sportRecord?.losses || 0,
      draws: sportRecord?.draws || 0,
      totalGames: sportGames,
      winRate: sportWinRate,
      elo: stats.sport_elos?.[sportKey] || stats.elo,
    };
  };

  const currentData = getSportData();

  return (
    <div className={styles.page}>
      <PageInfo
        title="Multiplayer Stats - Crossover Grid"
        metaTitle="Multiplayer Stats - Track Your ELO & Game History | Crossover Grid"
        metaDescription="View your multiplayer stats, ELO ratings, win-loss records, and game history across NBA, NFL, NHL, and MLB. Track your performance and climb the leaderboard!"
        metaUrl={location.pathname}
        keywords={['multiplayer stats', 'ELO rating', 'sports trivia stats', 'game history', 'crossover grid stats', 'NBA stats', 'NFL stats', 'NHL stats', 'MLB stats']}
        canonicalLink="/stats"
      />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />

      <div className={styles.container}>
        <h1 className={styles.title}>Multiplayer Stats</h1>

        <div className={styles.sportTabs}>
          {sports.map((sport) => (
            <button
              key={sport.id}
              className={`${styles.sportTab} ${selectedSport === sport.id ? styles.active : ''}`}
              onClick={() => setSelectedSport(sport.id)}
            >
              {sport.label}
            </button>
          ))}
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{currentData.wins}</div>
            <div className={styles.statLabel}>Wins</div>
          </div>

          <div className={styles.stat}>
            <div className={styles.statValue}>{currentData.losses}</div>
            <div className={styles.statLabel}>Losses</div>
          </div>

          <div className={styles.stat}>
            <div className={styles.statValue}>{currentData.draws}</div>
            <div className={styles.statLabel}>Draws</div>
          </div>

          <div className={styles.stat}>
            <div className={styles.statValue}>{typeof currentData.winRate === 'number' ? currentData.winRate : currentData.winRate}%</div>
            <div className={styles.statLabel}>Win Rate</div>
          </div>

          {currentData.elo && (
            <div className={styles.stat}>
              <div className={styles.statValue}>{currentData.elo}</div>
              <div className={styles.statLabel}>ELO Rating</div>
            </div>
          )}

          {stats.avg_score != null && (
            <div className={styles.stat}>
              <div className={styles.statValue}>{stats.avg_score.toFixed(1)}</div>
              <div className={styles.statLabel}>Avg Score</div>
            </div>
          )}

          {stats.best_score != null && (
            <div className={styles.stat}>
              <div className={styles.statValue}>{stats.best_score}</div>
              <div className={styles.statLabel}>Best Score</div>
            </div>
          )}
        </div>

        <div className={styles.record}>
          <span className={styles.wins}>{currentData.wins}W</span>
          <span className={styles.separator}>-</span>
          <span className={styles.losses}>{currentData.losses}L</span>
          <span className={styles.separator}>-</span>
          <span className={styles.draws}>{currentData.draws}D</span>
        </div>

        <div className={styles.total}>
          {currentData.totalGames} {selectedSport === 'overall' ? 'total' : sports.find(s => s.id === selectedSport)?.label} games
        </div>

        <Link to="/leaderboard" className={styles.link}>
          View Leaderboard →
        </Link>

        {history.length > 0 && (
          <div className={styles.history}>
            <h2 className={styles.historyTitle}>Recent Games</h2>
            {history.map((game) => (
              <div key={game.game_id} className={styles.game}>
                <div className={styles.gameTop}>
                  <span className={styles.sport}>{game.sport_name}</span>
                  <span className={`${styles.result} ${styles[game.result]}`}>
                    {game.result.toUpperCase()}
                  </span>
                </div>
                <div className={styles.gameScore}>
                  <div>
                    <div className={styles.player}>You</div>
                    <div className={styles.score}>{game.user_score}</div>
                    <div className={game.elo_change >= 0 ? styles.eloUp : styles.eloDown}>
                      {game.elo_change >= 0 ? '+' : ''}{game.elo_change}
                    </div>
                  </div>
                  <div className={styles.vs}>VS</div>
                  <div>
                    <div className={styles.player}>{game.opponent?.username || 'Unknown'}</div>
                    <div className={styles.score}>{game.opponent?.score ?? '-'}</div>
                    <div className={game.opponent?.elo_change >= 0 ? styles.eloUp : styles.eloDown}>
                      {game.opponent?.elo_change >= 0 ? '+' : ''}{game.opponent?.elo_change ?? 0}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserStatsPage;
